import { useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { VenetianMask } from 'lucide-react'
import { Popover, PopoverAnchor, PopoverContent, PopoverTitle } from '@/components/ui/popover'

type Browser = 'firefox' | 'edge' | 'chrome' | 'safari' | 'other'

/** The browser in use, by the names its private window and link menu go by. */
function detectBrowser(): Browser {
  const ua = navigator.userAgent
  if (/Firefox|FxiOS/.test(ua)) return 'firefox'
  if (/Edg\//.test(ua)) return 'edge'
  if (/Chrome|CriOS|Chromium/.test(ua)) return 'chrome'
  if (/Macintosh|Mac OS X/.test(ua) && /Safari\//.test(ua)) return 'safari'
  return 'other'
}

interface MasqueradeLinkProps {
  /** The user to masquerade as. */
  username: string
  /** The support user to sign in with, when known. */
  supportUsername?: string
}

/**
 * Opens the masquerade sign-in for a user, prefilled. It only works in a
 * private window — a normal tab shares this session and would replace it —
 * and a page cannot open one itself, so a plain click explains how to.
 * Right-clicking with the browser's modifier held (Option in Safari, Shift
 * elsewhere) gets the browser's own menu with its private-window option.
 */
export function MasqueradeLink({ username, supportUsername }: MasqueradeLinkProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const browser = detectBrowser()
  const modifier = browser === 'safari' ? 'Option' : 'Shift'

  const params = new URLSearchParams({ masqUsername: username })
  if (supportUsername) params.set('supportUsername', supportUsername)
  const href = `${window.location.origin}/masquerade?${params}`

  const explain = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setOpen(true)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={t('masquerade.linkTitle')}
          aria-label={t('masquerade.linkTitle')}
          className="inline-flex items-center text-(--ad-text-muted) hover:text-(--ad-link)"
          onClick={explain}
          onAuxClick={explain}
          onContextMenu={(e) => {
            const passThrough = browser === 'safari' ? e.altKey : e.shiftKey
            if (passThrough) {
              e.stopPropagation()
              setOpen(false)
              return
            }
            explain(e)
          }}
        >
          <VenetianMask className="size-4" />
        </a>
      </PopoverAnchor>
      <PopoverContent
        side="right"
        className="flex flex-col gap-2 text-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <PopoverTitle>{t('masquerade.privateTitle')}</PopoverTitle>
        <p>
          {t('masquerade.privateOpen', { window: t(`masquerade.privateWindow.${browser}`) })}
        </p>
        <p className="text-(--ad-text-muted)">{t('masquerade.privateWhy')}</p>
        <p>
          {t('masquerade.privateHow', {
            browser: t(`masquerade.browser.${browser}`),
            modifier,
            option: t(`masquerade.menuOption.${browser}`),
          })}
        </p>
      </PopoverContent>
    </Popover>
  )
}
